import { useQueryClient } from 'react-query';
import { useGetQuery } from '../useGetQuery';

export const useInvestmentOptions = () => {
    const queryClient = useQueryClient();

    const url = '/investments/signup/options';

    const { isLoading, entries: investmentOptions, isFetching } = useGetQuery<
        ConfigData
    >({
        url,
        cacheKey: `investment-options`
    });

    // Products request
    const {
        isLoading: isProductsLoading,
        entries: products,
        isFetching: isProductsFetching
    } = useGetQuery<InvestmentProduct[]>({
        url: '/investments/products',
        cacheKey: `investment-products`
    });

    const invalidateOptionsCache = () => {
        queryClient.invalidateQueries({ queryKey: ['investment-options'] });
    };

    const invalidateProductsCache = () => {
        queryClient.invalidateQueries({ queryKey: ['investment-products'] });
    };

    return {
        isLoading: isLoading || isProductsLoading,
        investmentOptions,
        invalidateOptionsCache,
        isFetching: isFetching || isProductsFetching,
        products,
        invalidateProductsCache
    };
};

export type Gender = 'Male' | 'Female';

export type RelationshipType =
    | 'Guardian'
    | 'Aunt'
    | 'Brother'
    | 'Daughter'
    | 'Father'
    | 'Husband'
    | 'Mother'
    | 'Sister'
    | 'Son'
    | 'Uncle'
    | 'Wife';

export type IdType =
    | 'Birth Certificate'
    | 'International Passport'
    | 'Drivers License'
    | 'Voters Card'
    | 'NIN';

export type UtilityBillType =
    | 'Electricity'
    | 'Telephone'
    | 'Waste management'
    | 'Water corporation'
    | 'Estate management'
    | 'Bank statement'
    | 'Tenancy Agreement'
    | "Valid Driver's License";

export type EmploymentStatus =
    | 'Permanent'
    | 'Contract'
    | 'Self Employed'
    | 'Unemployed'
    | 'Retired';

export type IncomeRange =
    | 'Below - 1M'
    | '1M - 2M'
    | '2M - 5M'
    | '5M - 10M'
    | 'Above 10M';

export type RedemptionReason =
    | 'Business'
    | 'Rent'
    | 'Property purchase'
    | 'Entertainment'
    | 'Return on investment not enough'
    | 'Other investments(ARM)'
    | 'Other investment(outside ARM)'
    | 'Poor service';

export type RedemptionType = 'Full' | 'Partial';

export interface Bank {
    bankName: string;
    bankCode: string;
    branchCode: string;
}

export interface KycLevel {
    level: string;
    maximumSingleInvestmentAmount: string;
    maximumSingleRedemptionAmount: string;
    maximumCumulativeInvestmentAmount: string;
    onboardingRequirement: string[];
}

export type PoliticallyExposedPersonCategory =
    | 'Head Of State'
    | 'State Governors'
    | 'Local Government Chairmen'
    | 'Senior Politicians'
    | 'Senior Government Officials'
    | 'Judicial or Military Officials'
    | 'Senior Executives of state owned companies'
    | 'Important Political party officials'
    | 'Family members or close associates of PEPs'
    | 'Members of royal families'
    | 'Diplomats'
    | 'Directors and Board Members of Corporate Organizations';

export interface YearOfEstablishment {
    value: number;
    label: string;
}

export interface CategoryOfBusiness {
    value: number;
    label: string;
}

export interface NumberOfEmployee {
    value: number;
    label: string;
}

export interface RequiredDocument {
    value: string;
    label: string;
}

export interface ClientSubType {
    value: number;
    label: string;
}

export interface ClientType {
    value: number;
    label: string;
}

export interface ConfigData {
    genders: Gender[];
    relationshipTypes: RelationshipType[];
    idTypes: IdType[];
    utilityBillTypes: UtilityBillType[];
    employmentStatuses: EmploymentStatus[];
    incomeRanges: IncomeRange[];
    redemptionReasons: RedemptionReason[];
    kycLevel: KycLevel[];
    politicallyExposedPersonsCategory: PoliticallyExposedPersonCategory[];
    banks: Bank[];
    redemptionTypes: RedemptionType[];
    yearOfEstablshment: YearOfEstablishment[];
    categoryOfBusiness: CategoryOfBusiness[];
    numbersOfEmployee: NumberOfEmployee[];
    requiredDocuments: RequiredDocument[];
    clientSubType: ClientSubType[];
    clientType: ClientType[];
}

export interface InvestmentProduct {
    code: string;
    currency: string;
    description: string;
    id: number;
    minimumInvestment: number;
    name: number;
}
